import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faBuilding, faBriefcase, faList, faPlus, faTags } from '@fortawesome/pro-solid-svg-icons';
import { isMobile } from 'helpers/mobile_helpers';
import { isBlank } from 'helpers/string_helpers';

import AssociatedContactSelector from './associated_contact_selector';
import AssociatedLeaseSpaceSelector from './associated_lease_space_selector';
import AssociatedProjectSelector from './associated_project_selector';
import AssociatedPropertySelector from './associated_property_selector';
import AssociatedCallListSelector from './associated_call_list_selector';
import AssociatedTagSelector from './associated_tag_selector';

export default class AssociatedModelSelectors extends React.Component {
  constructor(props) {
    super(props);

    this.wrapperRef = React.createRef();
  }

  static propTypes = {
    allowCallListSelect: PropTypes.bool,
    allowContactSelect: PropTypes.bool,
    allowProjectSelect: PropTypes.bool,
    allowPropertySelect: PropTypes.bool,
    allowTagSelect: PropTypes.bool,
    associatedModels: PropTypes.array,
    currentDatabaseId: PropTypes.number,
    isActivity: PropTypes.bool,
    selectedValue: PropTypes.object,
    taskListItems: PropTypes.array,
    taskTaggedRecords: PropTypes.array,
    taskType: PropTypes.string,
    onCreateCompany: PropTypes.func,
    onCreatePerson: PropTypes.func,
    onCreateTaskListItem: PropTypes.func,
    onCreateTaskTag: PropTypes.func,
    onModelAdded: PropTypes.func.isRequired,
    onTaskListAdded: PropTypes.func.isRequired,
    onTaskTagAdded: PropTypes.func.isRequired
  };

  state = {
    showingSelect: false
  }

  static defaultProps = {
    allowContactSelect: false,
    allowProjectSelect: false,
    allowPropertySelect: false,
    allowTagSelect: false,
    currentDatabaseId: null
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  isCall() {
    const { taskType } = this.props;
    return (taskType === 'call');
  }

  handleClickOutside = (event) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ showingSelect: false });
    }
  }

  handleShow = (e, type) => {
    e.preventDefault();

    this.setState({ showingSelect: type });
  }

  handleNewTaskListModel = (model) => {
    this.setState({ showingSelect: false });
    this.props.onTaskListAdded(model);
  }

  handleNewTagModel = (model) => {
    this.setState({ showingSelect: false });
    this.props.onTaskTagAdded(model);
  }

  handleNewAssociatedModel = (model) => {
    this.setState({ showingSelect: false });
    this.props.onModelAdded(model);
  }

  showLeaseSpaceSelector = () => {
    return this.props.associatedModels.some(m => m.associatedModelType === 'Project' && !m._destroy);
  }

  render() {
    return (
      <div ref={this.wrapperRef}>
        {this.renderInnerContents()}
      </div>
    );
  }

  renderContactSelector(contactType) {
    const {
      currentDatabaseId,
      selectedValue,
      onCreateCompany,
      onCreatePerson
    } = this.props;
    const currentDatabase = currentDatabaseId ? [{ id: currentDatabaseId }] : null;

    return (
      <div className="mb-4">
        <AssociatedContactSelector
          autoFocus
          databases={currentDatabase}
          selectedValue={selectedValue}
          type={contactType}
          onCreateContact={contactType === 'Contacts::Person' ? onCreatePerson : onCreateCompany}
          onModelAdded={this.handleNewAssociatedModel}
        />
      </div>
    );
  }

  renderInnerContents() {
    const {
      currentDatabaseId,
      associatedModels,
      taskListItems,
      taskTaggedRecords,
      onCreateTaskTag,
      onCreateTaskListItem
    } = this.props;
    const { showingSelect } = this.state;
    const selectedProjectIds = associatedModels
      .filter(m => m.associatedModelType === 'Project' && !m._destroy)
      .map(p => p.associatedModelId);

    switch (showingSelect) {
    case 'callList':
      return (
        <AssociatedCallListSelector
          callListItems={taskListItems}
          onCreateTaskListItem={onCreateTaskListItem}
          onModelAdded={this.handleNewTaskListModel}
        />
      );
    case 'contacts':
      return this.renderContactSelector('Contacts::Person');
    case 'companies':
      return this.renderContactSelector('Contacts::Company');
    case 'projects':
      return (
        <AssociatedProjectSelector
          onModelAdded={this.handleNewAssociatedModel}
        />
      );
    case 'leaseSpaces':
      return (
        <AssociatedLeaseSpaceSelector
          projectIds={selectedProjectIds}
          onModelAdded={this.handleNewAssociatedModel}
        />
      );
    case 'properties':
      return (
        <div className="mb-4">
          <AssociatedPropertySelector
            allDatabases={isBlank(currentDatabaseId)}
            onModelAdded={this.handleNewAssociatedModel}
          />
        </div>
      );
    case 'tags':
      return (
        <AssociatedTagSelector
          taskTaggedRecords={taskTaggedRecords}
          onCreateTaskTag={onCreateTaskTag}
          onModelAdded={this.handleNewTagModel}
        />
      );
    default: {
      if (isMobile()) {
        if (this.props.isActivity)
          return this.renderActivityMobileLinkList();
        else
          return this.renderMobileLinkList();
      } else {
        return this.renderLinkList();
      }
    }
    }
  }

  renderActivityMobileLinkList() {
    const { allowContactSelect, allowProjectSelect, allowPropertySelect, allowTagSelect } = this.props;
    return (
      <div className="links-list">
        <div className="w-100 bottom-buffer-sm with-height">
          <div className="col-xs-6 pl-0 pb-3">
            {allowPropertySelect &&
              this.renderMobileLink('Add Company', 'add-company', 'companies', faBriefcase)
            }
          </div>
          <div className="col-xs-6 pl-0 pb-3">
            {allowContactSelect &&
              this.renderMobileLink('Add Contact', 'add-contact', 'contacts', faUser)
            }
          </div>
          <div className="col-xs-6 pl-0 pb-3">
            {allowContactSelect &&
              this.renderMobileLink('Add Property', 'add-property', 'properties', faBuilding)
            }
          </div>
          <div className="col-xs-6 pl-0 pb-3">
            {allowProjectSelect &&
              this.renderMobileLink('Add Project', 'add-project', 'projects', faBuilding)
            }
          </div>
          <div className="col-xs-6 pl-0 pb-3">
            {this.showLeaseSpaceSelector() &&
              this.renderMobileLink('Add Space', 'add-lease-space', 'leaseSpaces', faBuilding)
            }
          </div>
          <div className="col-xs-6 pl-0 pb-3">
            {allowTagSelect &&
              this.renderMobileLink('Add Tag', 'add-tag', 'tags', faTags)
            }
          </div>
        </div>
      </div>
    );
  }

  renderMobileLinkList() {
    const {
      allowCallListSelect,
      allowContactSelect,
      allowProjectSelect,
      allowPropertySelect,
      allowTagSelect
    } = this.props;

    return (
      <div className="links-list">
        <div className="w-100 bottom-buffer-sm d-flex flex-wrap">
          {allowContactSelect &&
            this.renderMobileLink('Property', 'add-property', 'properties', faBuilding)
          }
          {allowContactSelect &&
            this.renderMobileLink('Contact', 'add-contact', 'contacts', faUser)
          }
          {allowPropertySelect &&
            this.renderMobileLink('Company', 'add-company', 'companies', faBriefcase)
          }
          {allowProjectSelect &&
            this.renderMobileLink('Project', 'add-project', 'projects', faBuilding)
          }
          {this.showLeaseSpaceSelector() &&
            this.renderMobileLink('Space', 'add-lease-space', 'leaseSpaces', faBuilding)
          }
          {allowCallListSelect && this.isCall() &&
            this.renderMobileLink('List', 'add-call-list', 'callList', faList)
          }
          {allowTagSelect &&
            this.renderMobileLink('Tag', 'add-tag', 'tags', faTags)
          }
        </div>
      </div>
    );
  }

  renderMobileLink(text, name, showType, icon) {
    return (
      <a
        className="association-btn mt-2 pb-1"
        href="#"
        key={name}
        name={name}
        onClick={e => this.handleShow(e, showType)}
      >
        <div className="d-inline-flex text-center">
          <FontAwesomeIcon className="ml-1 mt-1 right-buffer-sm" icon={icon} size="sm" />
          {text}
        </div>
      </a>
    );
  }

  renderLinkList() {
    const {
      allowCallListSelect,
      allowContactSelect,
      allowProjectSelect,
      allowPropertySelect,
      allowTagSelect
    } = this.props;
    const links = [];

    if (allowContactSelect) {
      links.push(
        <React.Fragment>
          {this.renderAddLink('Contact', 'add-contact', 'contacts')}
          {this.renderAddLink('Company', 'add-company', 'companies')}
        </React.Fragment>
      );
    }

    if (allowPropertySelect) {
      links.push(this.renderAddLink('Property', 'add-property', 'properties'));
    }

    if (allowProjectSelect) {
      links.push(this.renderAddLink('Project', 'add-project', 'projects'));
    }

    if (this.showLeaseSpaceSelector()) {
      links.push(this.renderAddLink('Space', 'add-lease-space', 'leaseSpaces'));
    }

    if (allowCallListSelect && this.isCall()) {
      links.push(this.renderAddLink('List', 'add-call-list', 'callList'));
    }

    if (allowTagSelect) {
      links.push(this.renderAddLink('Tag', 'add-tag', 'tags'));
    }

    if (links.length === 0) return null;
    return (
      <div className="links-list">
        <div className="links">
          {links}
        </div>
      </div>
    );
  }

  renderAddLink(text, name, showType) {
    return (
      <a className="mr-5" href="#" key={name} name={name} onClick={e => this.handleShow(e, showType)}>
        <FontAwesomeIcon icon={faPlus} size="sm"/> &nbsp;{text}
      </a>
    );
  }
}
